import { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Link } from 'react-router-dom';
import { analyticsApi, auth } from '@/services/api';
import { useApiData } from '@/hooks/useApiData';
import {
  dashboardStats, leads, properties, reminders,
  activityFeed,
} from '@/mocks/dashboard';

// ── Chart helpers ──

function MiniSparkline({ data, color, height = 44 }: { data: number[]; color: string; height?: number }) {
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * 100},${100 - (v / max) * 100}`).join(' ');
  const strokes: Record<string, string> = {
    accent: 'oklch(var(--accent-500))', primary: 'oklch(var(--primary-500))',
    secondary: 'oklch(var(--secondary-500))', teal: '#14b8a6', rose: '#f43f5e',
  };
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }} preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke={strokes[color] || strokes.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AreaChart({ data, labels, color, height = 140 }: { data: number[]; labels: string[]; color: string; height?: number }) {
  const max = Math.max(...data, 1);
  const stepX = 80 / (data.length - 1);
  const pts = data.map((v, i) => `${10 + i * stepX},${90 - (v / max) * 75}`).join(' ');
  const area = `M10,90 ${pts} L${10 + (data.length - 1) * stepX},90 Z`;
  const colorMap: Record<string, { fill: string; stroke: string }> = {
    accent: { fill: 'oklch(var(--accent-500) / 0.2)', stroke: 'oklch(var(--accent-500))' },
    primary: { fill: 'oklch(var(--primary-500) / 0.2)', stroke: 'oklch(var(--primary-500))' },
    secondary: { fill: 'oklch(var(--secondary-500) / 0.2)', stroke: 'oklch(var(--secondary-500))' },
    teal: { fill: 'rgba(20,184,166,0.15)', stroke: '#14b8a6' },
    rose: { fill: 'rgba(244,63,94,0.15)', stroke: '#f43f5e' },
  };
  const c = colorMap[color] || colorMap.accent;
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }} preserveAspectRatio="none">
      <path d={area} fill={c.fill} />
      <polyline points={pts} fill="none" stroke={c.stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {data.map((v, i) => (
        <g key={i}>
          <circle cx={10 + i * stepX} cy={90 - (v / max) * 75} r="1.8" fill={c.stroke} />
          <text x={10 + i * stepX} y={97} textAnchor="middle" fontSize={4.5} fill="oklch(var(--foreground-400))" fontFamily="system-ui">{labels[i]}</text>
        </g>
      ))}
    </svg>
  );
}

function BarChart({ data, labels, color, height = 120 }: { data: number[]; labels: string[]; color: string; height?: number }) {
  const max = Math.max(...data, 1);
  const barW = 80 / data.length;
  const gap = 20 / data.length;
  const colors: Record<string, string> = {
    accent: 'oklch(var(--accent-500) / 0.7)', primary: 'oklch(var(--primary-500) / 0.7)',
    secondary: 'oklch(var(--secondary-500) / 0.7)', teal: 'rgba(20,184,166,0.6)', rose: 'rgba(244,63,94,0.6)',
  };
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }} preserveAspectRatio="none">
      {data.map((v, i) => {
        const h = (v / max) * 75;
        const x = 10 + i * (barW + gap);
        return (
          <g key={i}>
            <rect x={x} y={90 - h} width={barW} height={h} rx="1.5" fill={colors[color] || colors.accent} />
            <text x={x + barW / 2} y={96} textAnchor="middle" fontSize={5} fill="oklch(var(--foreground-400))" fontFamily="system-ui">{labels[i]}</text>
          </g>
        );
      })}
    </svg>
  );
}

function DonutChart({ segments, size = 100 }: { segments: { label: string; value: number; color: string }[]; size?: number }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  const radius = 35;
  const center = 50;
  let cumAngle = 0;

  const colors: Record<string, string> = {
    accent: 'oklch(var(--accent-500))', primary: 'oklch(var(--primary-500))',
    secondary: 'oklch(var(--secondary-500))', teal: '#14b8a6', rose: '#f43f5e',
    blue: '#3b82f6', amber: '#f59e0b', green: '#22c55e',
  };

  function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
    const startRad = (startAngle - 90) * Math.PI / 180;
    const endRad = (endAngle - 90) * Math.PI / 180;
    const x1 = cx + r * Math.cos(startRad), y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad), y2 = cy + r * Math.sin(endRad);
    const large = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
  }

  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height: size }} preserveAspectRatio="xMidYMid meet">
      {segments.map((seg, i) => {
        const sliceAngle = (seg.value / total) * 360;
        const start = cumAngle;
        cumAngle += sliceAngle;
        return <path key={i} d={describeArc(center, center, radius, start, cumAngle)} fill={colors[seg.color] || colors.accent} opacity={0.8} />;
      })}
      <circle cx={center} cy={center} r={22} fill="white" />
      <text x={center} y={center - 4} textAnchor="middle" fontSize={10} fontWeight="bold" fill="oklch(var(--foreground-950))" fontFamily="system-ui">{total}</text>
      <text x={center} y={center + 8} textAnchor="middle" fontSize={5} fill="oklch(var(--foreground-400))" fontFamily="system-ui">Total</text>
    </svg>
  );
}

function formatINR(val: number | string | null | undefined): string {
  const n = val === null || val === undefined ? 0 : Number(val);
  if (Number.isNaN(n)) return '\u20B90';
  if (n >= 10000000) return '\u20B9' + (n / 10000000).toFixed(1) + 'Cr';
  if (n >= 100000) return '\u20B9' + (n / 100000).toFixed(1) + 'L';
  if (n >= 1000) return '\u20B9' + (n / 1000).toFixed(0) + 'K';
  return '\u20B9' + n.toFixed(0);
}

function formatNumber(val: number | string | null | undefined): string {
  const n = val === null || val === undefined ? 0 : Number(val);
  if (Number.isNaN(n)) return '0';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return n.toString();
}

function toNum(val: number | string | null | undefined): number {
  if (val === null || val === undefined) return 0;
  const n = Number(val);
  return Number.isNaN(n) ? 0 : n;
}

function toInt(val: number | string | null | undefined): number {
  if (val === null || val === undefined) return 0;
  const n = parseInt(String(val), 10);
  return Number.isNaN(n) ? 0 : n;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  return formatDate(dateStr);
}

const shortMonths = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

interface AnalyticsSummary {
  listings: {
    total: number; available: number; under_review: number; leased: number;
    sold: number; total_views: number; total_rent_value: number; avg_size_acres: number;
  };
  inquiries: {
    total: number; new_count: number; active_count: number; converted_count: number; spam_count: number;
  };
  monthly_inquiries: { month: string; count: number }[];
  monthly_revenue: { month: string; total_rent: number }[];
  category_breakdown: { category: string; count: number }[];
  city_breakdown: { city: string; count: number }[];
  latest_inquiries: { id: number; name: string; email: string; subject: string; status: string; source: string; created_at: string; listing_title: string | null }[];
  top_viewed: { id: number; title: string; city: string; category: string; view_count: number; expected_rent: number; rent_currency: string; rent_period: string }[];
}

export default function AnalyticsPage() {
  const isAuthed = auth.isAuthenticated();
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [pulseOn, setPulseOn] = useState(true);

  const { data: apiSummary, loading, error } = useApiData<AnalyticsSummary>(
    () => analyticsApi.summary().then(r => r.data as unknown as AnalyticsSummary),
    [isAuthed]
  );

  // True only when the backend actually returned live analytics data with valid numbers
  const isLiveData = !!apiSummary && !error && typeof apiSummary.listings?.total === 'number' && apiSummary.listings.total > 0;

  // Auto-pulse toggle every 8 seconds for "live" feel
  useEffect(() => {
    if (!pulseOn) return;
    const interval = setInterval(() => setPulseOn(false), 8000);
    return () => clearInterval(interval);
  }, [pulseOn]);

  // Merge API data with mock fallback — only merge fields that have real values
  const summary = useMemo(() => {
    const def: AnalyticsSummary = {
      listings: {
        total: dashboardStats.totalProperties, available: dashboardStats.activeListings,
        under_review: 18, leased: 32, sold: 14,
        total_views: 48203, total_rent_value: dashboardStats.revenue, avg_size_acres: 3.2,
      },
      inquiries: {
        total: dashboardStats.totalLeadsThisMonth, new_count: 28, active_count: 35,
        converted_count: 18, spam_count: 13,
      },
      monthly_inquiries: shortMonths.map((m, i) => ({ month: m, count: [42, 48, 55, 51, 62, 68, 72, 65, 78, 82, 88, 94][i] })),
      monthly_revenue: shortMonths.map((m, i) => ({ month: m, total_rent: [2.8, 3.1, 3.5, 3.2, 4.0, 4.5, 5.2, 4.8, 5.5, 5.0, 5.8, 6.2][i] * 100000 })),
      category_breakdown: [
        { category: 'Commercial', count: 5 }, { category: 'Residential', count: 4 },
        { category: 'Industrial', count: 3 }, { category: 'Land', count: 3 },
      ],
      city_breakdown: [
        { city: 'Bangalore', count: 5 }, { city: 'Mumbai', count: 3 },
        { city: 'Delhi', count: 1 }, { city: 'Pune', count: 1 },
        { city: 'Hyderabad', count: 1 }, { city: 'Chennai', count: 1 },
      ],
      latest_inquiries: leads.slice(0, 5).map(l => ({ id: l.id, name: l.name, email: l.email, subject: l.propertyInterest, status: (l.status || 'New Lead').toLowerCase().replace(/ /g, '_'), source: l.source, created_at: l.createdAt, listing_title: l.propertyInterest })),
      top_viewed: properties.slice(0, 5).map(p => ({
        id: p.id, title: p.title, city: p.city, category: p.type,
        view_count: p.enquiries ? p.enquiries.length * 120 + 50 : Math.floor(Math.random() * 500 + 100),
        expected_rent: p.price, rent_currency: 'INR', rent_period: 'monthly',
      })),
    };

    if (!apiSummary) return def;

    // Only override defaults with defined, non-null API values
    const pick = <T,>(api: T | undefined | null, fallback: T): T =>
      api !== undefined && api !== null ? api : fallback;

    const merged: AnalyticsSummary = {
      listings: {
        total: pick(toInt(apiSummary.listings?.total), def.listings.total),
        available: pick(toInt(apiSummary.listings?.available), def.listings.available),
        under_review: pick(toInt(apiSummary.listings?.under_review), def.listings.under_review),
        leased: pick(toInt(apiSummary.listings?.leased), def.listings.leased),
        sold: pick(toInt(apiSummary.listings?.sold), def.listings.sold),
        total_views: pick(toInt(apiSummary.listings?.total_views), def.listings.total_views),
        total_rent_value: pick(toNum(apiSummary.listings?.total_rent_value), def.listings.total_rent_value),
        avg_size_acres: pick(toNum(apiSummary.listings?.avg_size_acres), def.listings.avg_size_acres),
      },
      inquiries: {
        total: pick(toInt(apiSummary.inquiries?.total), def.inquiries.total),
        new_count: pick(toInt(apiSummary.inquiries?.new_count), def.inquiries.new_count),
        active_count: pick(toInt(apiSummary.inquiries?.active_count), def.inquiries.active_count),
        converted_count: pick(toInt(apiSummary.inquiries?.converted_count), def.inquiries.converted_count),
        spam_count: pick(toInt(apiSummary.inquiries?.spam_count), def.inquiries.spam_count),
      },
      monthly_inquiries: apiSummary.monthly_inquiries?.length ? apiSummary.monthly_inquiries.map(i => ({ month: i.month, count: toInt(i.count) })) : def.monthly_inquiries,
      monthly_revenue: apiSummary.monthly_revenue?.length ? apiSummary.monthly_revenue.map(i => ({ month: i.month, total_rent: toNum(i.total_rent) })) : def.monthly_revenue,
      category_breakdown: apiSummary.category_breakdown?.length ? apiSummary.category_breakdown.map(c => ({ category: c.category, count: toInt(c.count) })) : def.category_breakdown,
      city_breakdown: apiSummary.city_breakdown?.length ? apiSummary.city_breakdown.map(c => ({ city: c.city, count: toInt(c.count) })) : def.city_breakdown,
      latest_inquiries: apiSummary.latest_inquiries?.length ? apiSummary.latest_inquiries : def.latest_inquiries,
      top_viewed: apiSummary.top_viewed?.length ? apiSummary.top_viewed.map(v => ({ ...v, view_count: toInt(v.view_count), expected_rent: toNum(v.expected_rent) })) : def.top_viewed,
    };
    return merged;
  }, [apiSummary]);

  const s = summary;

  // Derived stats
  const totalViews = s.listings.total_views;
  const conversionRate = s.inquiries.total > 0 ? Math.round((s.inquiries.converted_count / s.inquiries.total) * 100 * 10) / 10 : 0;
  const viewsPerListing = s.listings.total > 0 ? Math.round(totalViews / s.listings.total) : 0;

  const categoryColors = ['accent', 'primary', 'secondary', 'teal', 'rose'];
  const cityColors = ['accent', 'primary', 'secondary', 'teal', 'rose', 'blue'];

  const inquiryTrendData = s.monthly_inquiries.map(i => i.count);
  const inquiryTrendLabels = s.monthly_inquiries.map(i => i.month);
  const revenueTrendData = s.monthly_revenue.map(i => Math.round(i.total_rent / 100000));
  const revenueTrendLabels = s.monthly_revenue.map(i => i.month);

  const donutSegments = s.category_breakdown.map((cat, i) => ({
    label: cat.category,
    value: cat.count,
    color: categoryColors[i % categoryColors.length],
  }));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-5">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-background-800 rounded w-48" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-28 bg-background-800 rounded-xl" />
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="h-52 bg-background-800 rounded-xl" />
              <div className="h-52 bg-background-800 rounded-xl" />
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="font-heading text-xl font-bold text-foreground-950">Analytics Overview</h1>
              <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                isLiveData
                  ? (pulseOn ? 'bg-teal-500/10 text-teal-400 border border-teal-500/20' : 'bg-background-200 text-foreground-500')
                  : 'bg-secondary-100 text-secondary-900 border border-secondary-200'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isLiveData ? (pulseOn ? 'bg-teal-400 animate-pulse' : 'bg-foreground-400') : 'bg-secondary-500'}`} />
                {isLiveData ? (pulseOn ? 'Live' : 'Paused') : 'Sample'}
              </span>
            </div>
            <p className="text-xs text-foreground-500">Listing views, inquiry trends, and revenue stats — {isLiveData ? 'updated in real time' : 'demo figures until the backend is connected'}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-background-50 rounded-xl p-1 flex items-center gap-1">
              {(['7d', '30d', '90d'] as const).map(d => (
                <button key={d} onClick={() => { setTimeRange(d); setPulseOn(true); }} className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap cursor-pointer transition-all ${timeRange === d ? 'bg-accent-100 text-accent-700 font-semibold' : 'text-foreground-500 hover:text-foreground-950'}`}>
                  {d === '7d' ? '7 Days' : d === '30d' ? '30 Days' : '90 Days'}
                </button>
              ))}
            </div>
            <button onClick={() => setPulseOn(!pulseOn)} className="w-8 h-8 rounded-lg bg-background-50 text-foreground-400 hover:text-foreground-700 cursor-pointer flex items-center justify-center border border-background-200" title={pulseOn ? 'Pause live updates' : 'Resume live updates'}>
              <i className={pulseOn ? 'ri-pause-line' : 'ri-play-line'} />
            </button>
          </div>
        </div>

        {/* ── KPI ROW ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Listings', value: s.listings.total.toString(), sub: `${s.listings.available} active`, icon: 'ri-building-4-line', color: 'accent', sparkline: [12, 18, 22, 28, 32, 35, 38], change: dashboardStats.statsChange.totalProperties },
            { label: 'Total Views', value: formatNumber(totalViews), sub: viewsPerListing + ' per listing', icon: 'ri-eye-line', color: 'primary', sparkline: [150, 320, 480, 620, 850, 1020, 1350], change: 22.5 },
            { label: 'Total Inquiries', value: s.inquiries.total.toString(), sub: `${conversionRate}% conversion`, icon: 'ri-mail-line', color: 'secondary', sparkline: [3, 7, 5, 9, 11, 14, 17], change: dashboardStats.statsChange.totalLeadsThisMonth },
            { label: 'Revenue', value: formatINR(s.listings.total_rent_value), sub: `${s.listings.leased} leased`, icon: 'ri-money-rupee-circle-line', color: 'teal', sparkline: [2, 4, 6, 8, 12, 15, 22], change: dashboardStats.statsChange.revenue },
          ].map(kpi => (
            <div key={kpi.label} className="rounded-xl border bg-white border-background-200/60 p-4 hover:border-accent-200 hover:-translate-y-0.5 transition-all cursor-default group">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider group-hover:text-accent-700 transition-colors">{kpi.label}</span>
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                  kpi.color === 'accent' ? 'bg-accent-100 text-accent-700' :
                  kpi.color === 'primary' ? 'bg-primary-500/15 text-primary-700' :
                  kpi.color === 'secondary' ? 'bg-secondary-500/15 text-secondary-700' :
                  'bg-teal-500/15 text-teal-400'
                }`}>
                  <i className={`${kpi.icon} text-xs`} />
                </span>
              </div>
              <p className="font-heading text-xl font-bold text-foreground-950 mb-1">{kpi.value}</p>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-semibold ${kpi.change >= 0 ? 'text-teal-400' : 'text-rose-400'}`}>
                  {kpi.change >= 0 ? '+' : ''}{kpi.change}%
                </span>
                <span className="text-[10px] text-foreground-500">vs last period</span>
              </div>
              <div className="mt-2">
                <MiniSparkline data={kpi.sparkline} color={kpi.color} height={32} />
              </div>
            </div>
          ))}
        </div>

        {/* ── MAIN CHARTS ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Inquiry Trend */}
          <div className="rounded-xl border border-background-200/60 bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground-950">Inquiry Volume Trend</h3>
                <p className="text-[10px] text-foreground-500">Monthly inquiry submissions</p>
              </div>
              <span className="text-[10px] text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded-full">+24% YoY</span>
            </div>
            <AreaChart data={inquiryTrendData.slice(-6)} labels={inquiryTrendLabels.slice(-6)} color="accent" height={140} />
          </div>

          {/* Revenue Trend */}
          <div className="rounded-xl border border-background-200/60 bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground-950">Revenue Trend</h3>
                <p className="text-[10px] text-foreground-500">Monthly listing rent value (₹L)</p>
              </div>
              <span className="text-[10px] text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded-full">+18% YoY</span>
            </div>
            <BarChart data={revenueTrendData.slice(-6)} labels={revenueTrendLabels.slice(-6)} color="primary" height={140} />
          </div>
        </div>

        {/* ── WIDGET ROW: Category + City + Top Viewed ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Category Distribution */}
          <div className="rounded-xl border border-background-200/60 bg-white p-5">
            <h3 className="text-sm font-semibold text-foreground-950 mb-4">Property Categories</h3>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 flex-shrink-0">
                <DonutChart segments={donutSegments} size={96} />
              </div>
              <div className="flex-1 space-y-2">
                {donutSegments.map((seg, i) => (
                  <div key={seg.label} className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${seg.color === 'accent' ? 'bg-accent-500' : seg.color === 'primary' ? 'bg-primary-500' : seg.color === 'secondary' ? 'bg-secondary-500' : seg.color === 'teal' ? 'bg-teal-500' : 'bg-rose-500'}`} />
                    <span className="text-[11px] text-foreground-500 flex-1">{seg.label}</span>
                    <span className="text-[11px] font-semibold text-foreground-950">{seg.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* City Breakdown */}
          <div className="rounded-xl border border-background-200/60 bg-white p-5">
            <h3 className="text-sm font-semibold text-foreground-950 mb-4">Top Cities</h3>
            <div className="space-y-2.5">
              {s.city_breakdown.map((city, i) => {
                const maxCount = s.city_breakdown[0]?.count || 1;
                const pct = Math.round((city.count / maxCount) * 100);
                return (
                  <div key={city.city} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-bold ${
                          i === 0 ? 'bg-accent-100 text-accent-700' : i === 1 ? 'bg-primary-500/15 text-primary-700' : 'bg-background-200 text-foreground-500'
                        }`}>{i + 1}</span>
                        <span className="text-xs text-foreground-950">{city.city}</span>
                      </div>
                      <span className="text-xs font-semibold text-foreground-950">{city.count}</span>
                    </div>
                    <div className="h-1.5 bg-background-200/60 rounded-full overflow-hidden ml-8">
                      <div className={`h-full rounded-full ${['bg-accent-500','bg-primary-500','bg-secondary-500','bg-teal-500','bg-rose-500'][i % 5]}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Listing Status Breakdown */}
          <div className="rounded-xl border border-background-200/60 bg-white p-5">
            <h3 className="text-sm font-semibold text-foreground-950 mb-4">Listing Status</h3>
            <div className="space-y-3">
              {[
                { label: 'Available', value: s.listings.available, color: 'teal', icon: 'ri-checkbox-circle-line' },
                { label: 'Under Review', value: s.listings.under_review, color: 'amber', icon: 'ri-time-line' },
                { label: 'Leased', value: s.listings.leased, color: 'accent', icon: 'ri-file-text-line' },
                { label: 'Sold', value: s.listings.sold, color: 'green', icon: 'ri-check-double-line' },
              ].map(status => {
                const max = Math.max(...[s.listings.available, s.listings.under_review, s.listings.leased, s.listings.sold], 1);
                const pct = Math.round((status.value / max) * 100);
                const colorMap: Record<string, string> = { teal: 'bg-teal-500', amber: 'bg-amber-500', accent: 'bg-accent-500', green: 'bg-green-500' };
                const textMap: Record<string, string> = { teal: 'text-teal-400', amber: 'text-amber-500', accent: 'text-accent-700', green: 'text-green-500' };
                return (
                  <div key={status.label} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                        <i className={`${status.icon} ${textMap[status.color]} text-[10px]`} /> {status.label}
                      </span>
                      <span className="text-xs font-semibold text-foreground-950">{status.value}</span>
                    </div>
                    <div className="h-2 bg-background-200/60 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${colorMap[status.color]}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── BOTTOM ROW: Top Viewed + Latest Inquiries + Activity Feed ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Top Viewed Listings */}
          <div className="rounded-xl border border-background-200/60 bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground-950">Most Viewed Listings</h3>
              <Link to="/dashboard/properties" className="text-[10px] font-semibold text-accent-700 hover:text-accent-300 whitespace-nowrap">View All</Link>
            </div>
            <div className="space-y-3">
              {s.top_viewed.map((item, i) => (
                <Link key={item.id} to={`/listings/${item.id}`} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-background-50 transition-colors cursor-pointer group">
                  <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                    i === 0 ? 'bg-accent-100 text-accent-700' : i === 1 ? 'bg-primary-500/15 text-primary-700' : 'bg-background-200/60 text-foreground-500'
                  }`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground-950 truncate group-hover:text-accent-700 transition-colors">{item.title}</p>
                    <p className="text-[10px] text-foreground-500">{item.city} &middot; {item.category}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-xs font-bold text-foreground-950">{formatINR(item.expected_rent)}</span>
                    <p className="text-[10px] text-foreground-400">{formatNumber(item.view_count)} views</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Latest Inquiries */}
          <div className="rounded-xl border border-background-200/60 bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground-950">Latest Inquiries</h3>
              <Link to="/dashboard/leads" className="text-[10px] font-semibold text-accent-700 hover:text-accent-300 whitespace-nowrap">View All</Link>
            </div>
            <div className="space-y-2.5">
              {s.latest_inquiries.length === 0 ? (
                <p className="text-xs text-foreground-500 text-center py-6">No inquiries yet</p>
              ) : (
                (Array.isArray(s.latest_inquiries) ? s.latest_inquiries.slice(0, 5) : []).map((inq: Record<string, unknown>) => (
                  <div key={inq.id as number} className="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-background-50 transition-colors">
                    <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${(inq.status as string) === 'new' ? 'bg-teal-500' : (inq.status as string) === 'converted' ? 'bg-green-500' : 'bg-accent-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground-950 truncate">{inq.name as string}</p>
                      <p className="text-[10px] text-foreground-500 truncate">{inq.subject || inq.listing_title || 'General Inquiry'}</p>
                    </div>
                    <span className="text-[9px] text-foreground-400 flex-shrink-0">{formatTime(inq.created_at as string)}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Real-Time Activity */}
          <div className="rounded-xl border border-background-200/60 bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground-950">Real-Time Activity</h3>
              <Link to="/dashboard/crm" className="text-[10px] font-semibold text-accent-700 hover:text-accent-300 whitespace-nowrap">CRM</Link>
            </div>
            <div className="space-y-2">
              {activityFeed.slice(0, 6).map(event => {
                const iconMap: Record<string, string> = {
                  lead_status_change: 'ri-arrow-up-circle-line', deal_closed: 'ri-check-double-line',
                  inquiry_received: 'ri-mail-add-line', lead_scored: 'ri-bar-chart-line',
                  property_update: 'ri-building-line', deal_movement: 'ri-swap-line',
                  reminder_due: 'ri-alarm-line', property_listed: 'ri-add-circle-line',
                  lead_created: 'ri-user-add-line',
                };
                const colorMap: Record<string, string> = {
                  lead_status_change: 'bg-accent-100 text-accent-700',
                  deal_closed: 'bg-green-500/15 text-green-500',
                  inquiry_received: 'bg-primary-500/15 text-primary-700',
                  lead_scored: 'bg-teal-500/15 text-teal-400',
                  property_update: 'bg-secondary-500/15 text-secondary-700',
                  deal_movement: 'bg-blue-500/15 text-blue-400',
                  reminder_due: 'bg-rose-500/15 text-rose-400',
                  property_listed: 'bg-amber-500/15 text-amber-500',
                  lead_created: 'bg-accent-100 text-accent-700',
                };
                return (
                  <div key={event.id} className="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-background-50 transition-colors">
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${colorMap[event.type] || 'bg-background-200 text-foreground-500'}`}>
                      <i className={`${iconMap[event.type] || 'ri-information-line'} text-[11px]`} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground-950 leading-relaxed">{event.title}</p>
                      <p className="text-[10px] text-foreground-500 truncate">{event.description}</p>
                      <p className="text-[9px] text-foreground-400 mt-0.5">{event.actor} &middot; {formatTime(event.timestamp)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── REMINDERS ── */}
        <div className="rounded-xl border border-background-200/60 bg-white p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-foreground-950">Pending Reminders</h3>
            <span className="text-[10px] text-foreground-500">{reminders.filter(r => r.status !== 'Snoozed').length} active</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
            {reminders.filter(r => r.status !== 'Snoozed').slice(0, 4).map(r => (
              <div key={r.id} className={`flex items-start gap-2.5 p-3 rounded-lg ${
                r.status === 'Overdue' ? 'bg-rose-500/5 border border-rose-500/15' : 'bg-background-200'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                  r.priority === 'High' ? 'bg-rose-500' : r.priority === 'Medium' ? 'bg-accent-500' : 'bg-secondary-500'
                }`} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-foreground-950 truncate">{r.text}</p>
                  <p className="text-[10px] text-foreground-500">{r.leadName} &middot; {formatDate(r.dueDateTime)}</p>
                  {r.status === 'Overdue' && <span className="inline-block text-[9px] font-semibold text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded-full mt-1">Overdue</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}